import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { PageLoader } from '@/shared/ui/page-loader';

// Define role-based route permissions
const ROLE_PERMISSIONS = {
  admin: [
    '/', '/projects', '/tasks', '/moderators', '/participants', 
    '/surveys', '/feedback', '/analytics', '/schedules', '/journal-entry',
    '/moderator-assignments', '/role-management', '/survey-data', '/sessions',
    '/moderator', '/moderator/tasks', '/moderator/availability', 
    '/moderator/participants', '/moderator/schedules', '/moderator/data-collection'
  ],
  project_manager: [
    '/', '/projects', '/tasks', '/moderators', '/participants', 
    '/surveys', '/feedback', '/analytics', '/schedules', '/journal-entry',
    '/moderator-assignments', '/role-management', '/survey-data', '/sessions',
    '/inventory'
  ],
  scheduler: [
    '/', '/projects', '/tasks', '/moderators', '/participants', 
    '/schedules', '/journal-entry', '/moderator-assignments'
  ],
  moderator: [
    '/', '/tasks', '/participants', '/journal-entry', '/schedules',
    '/moderator', '/moderator/tasks', '/moderator/availability', 
    '/moderator/participants', '/moderator/schedules', '/moderator/data-collection'
  ],
  participant: [
    '/participant', '/participant-schedules'
  ]
} as const;

type UserRole = 'admin' | 'project_manager' | 'moderator' | 'scheduler' | 'participant';

// Ensure we only use supported roles
const isUserRole = (role: any): role is UserRole => {
  return ['admin', 'project_manager', 'moderator', 'scheduler', 'participant'].includes(role);
};

// Resolve the effective role: admin (with view-as), the user's own role, or an email-based fallback.
const resolveGuardRole = (
  user: { role?: any; email?: string },
  isAdmin: boolean,
  effectiveRole: any,
): UserRole => {
  const roleLower = (user.role || '').toString().toLowerCase();
  let role: UserRole | null = null;

  if (roleLower === 'admin' || isAdmin) {
    role = (effectiveRole ?? 'admin') as UserRole;
    if (!isUserRole(role)) role = 'admin';
  } else if (user.role && isUserRole(user.role)) {
    role = user.role as UserRole;
  } else if (user.email?.includes('@centific.com')) {
    role = 'project_manager';
  } else {
    role = 'participant';
  }

  // Safety: normalize unexpected values
  if (!isUserRole(role)) {
    console.warn('[RoleGuard] Unknown role detected, defaulting to participant:', role);
    role = 'participant';
  }
  return role;
};

// Path the user should be redirected to when denied access to the current path, else null.
// Admins can access any path.
const getRedirectForDeniedPath = (
  role: UserRole,
  allowedRoles: UserRole[],
  fallbackPath: string,
): string | null => {
  if (role === 'admin' || allowedRoles.includes(role)) return null;
  const allowedPaths: readonly string[] = ROLE_PERMISSIONS[role] ?? [];
  if (allowedPaths.includes(globalThis.location.pathname)) return null;
  return allowedPaths[0] ?? fallbackPath ?? '/';
};

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  fallbackPath?: string;
}

const RoleGuard = ({ 
  children, 
  allowedRoles = ['admin', 'project_manager', 'moderator', 'scheduler', 'participant'],
  fallbackPath = '/'
}: RoleGuardProps) => {
  const { user, isAdmin, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        if (!user) {
          setLoading(false);
          return;
        }

        const role = resolveGuardRole(user, isAdmin, effectiveRole);
        setUserRole(role);
        setLoading(false);

        // Redirect if the user has no access to the current path (admin can access any path)
        const redirectPath = getRedirectForDeniedPath(role, allowedRoles, fallbackPath);
        if (redirectPath) {
          navigate(redirectPath, { replace: true });
          toast({
            title: "Access Denied",
            description: "You don't have permission to access this page.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('[RoleGuard] Error checking user role:', error);
        setLoading(false);
      }
    };

    checkUserRole();
  }, [user, navigate, allowedRoles, toast, fallbackPath, isAdmin, effectiveRole]);

  if (loading) {
    return <PageLoader fullScreen />;
  }

  // User not in allowed roles for this route: show loader while redirect happens (avoids blank screen)
  if (!userRole || !allowedRoles.includes(userRole)) {
    return <PageLoader fullScreen />;
  }

  return <>{children}</>;
};

export default RoleGuard;
